import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ImagePlus,
  Loader2,
  Star,
  Trash2,
  RefreshCw,
  Image as ImageIcon,
  Filter as FilterIcon,
  X as XIcon,
  Tag as TagIcon,
  UploadCloud,
  Link2,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import GlassCard from "@/components/GlassCard";
import GradientMesh from "@/components/GradientMesh";
import ConfirmDialog from "@/components/ConfirmDialog";

// Reuse the same client-side downscale logic the rest of the app uses for
// image uploads so we don't suddenly accept multi-MB payloads here. ~1MB.
async function fileToCompressedDataUrl(file, maxDim = 1280, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const longest = Math.max(img.width, img.height);
        const scale = longest > maxDim ? maxDim / longest : 1;
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        try {
          resolve(canvas.toDataURL("image/jpeg", quality));
        } catch (e) {
          reject(e);
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * TagPicker — larger, higher-contrast chips.
 *
 * Redesign notes (Phase 16):
 *  • Larger touch target (36px+) so tags are easy to tap on mobile.
 *  • Removed uppercase micro-tracking; use normal-case labels.
 *  • Selected state uses solid brand tint (not a subtle 12%-opacity fill)
 *    so it's obvious at a glance which tags are on.
 *  • Passes through allTags so callers can control the default set (we
 *    intentionally do NOT show hardcoded "haircut" to every business).
 */
function TagPicker({ value, onChange, allTags }) {
  const norm = (t) => (t || "").trim().toLowerCase();
  const active = new Set(value.map(norm));
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label="Tags">
      {allTags.map((t) => {
        const isOn = active.has(norm(t));
        // Prettify: "before/after" → "Before / After", "customer result" → "Customer Result".
        const label = t
          .replace(/\//g, " / ")
          .replace(/(^|\s)\S/g, (c) => c.toUpperCase());
        return (
          <button
            key={t}
            type="button"
            onClick={() => {
              const next = isOn
                ? value.filter((v) => norm(v) !== norm(t))
                : [...value, t];
              onChange(next);
            }}
            aria-pressed={isOn}
            data-testid={`media-tag-pick-${t.replace(/[^a-z]+/g, "-")}`}
            className={
              "text-sm px-3 py-1.5 rounded-full border transition " +
              (isOn
                ? "bg-[color:var(--brand-primary)] border-[color:var(--brand-primary)] text-[color:var(--bg-0)] font-medium shadow-sm"
                : "bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-2)] hover:border-[color:var(--border-strong)] hover:text-[color:var(--text-1)]")
            }
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default function MediaLibraryPage() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ tags: [], max_items: 30 });
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filterTags, setFilterTags] = useState([]);
  const [search, setSearch] = useState("");

  // Upload dialog state
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState(""); // for showing filename in the thumbnail card
  const [externalUrl, setExternalUrl] = useState("");
  const [desc, setDesc] = useState("");
  const [tags, setTags] = useState([]);
  const [favorite, setFavorite] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Phase 16 redesign — additional dialog state.
  // ``selectedFiles`` is the pre-upload buffer for MULTI-file selections.
  // We show a thumbnail grid + a dynamic "Add N images" CTA before any
  // network call runs. For single-file selection we still route through
  // the standard editor (previewUrl + description + tags + favorite).
  const [selectedFiles, setSelectedFiles] = useState([]); // [{name, dataUrl, size}]
  const [preparingFiles, setPreparingFiles] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // Import-from-URL is collapsed by default — uploads are the primary path.
  const [urlOpen, setUrlOpen] = useState(false);
  // Description + Tags + Favorite live in a collapsed "Add details" section.
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Ref to the hidden <input type="file"/> so the drop area's "Choose image"
  // button can trigger the OS picker.
  const fileInputRef = useRef(null);

  // Bulk file upload state — used when the user picks multiple files at once.
  // We process them sequentially so the UI can show a clean N-of-M progress
  // line and so we never hit the per-doc base64 cap by sending parallel
  // large payloads.
  const [bulkQueue, setBulkQueue] = useState([]); // [{name, status, error}]
  const [bulkBusy, setBulkBusy] = useState(false);

  const ACCEPTED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
  const ACCEPTED_LABEL = "JPG, PNG or WEBP";

  const resetDialog = () => {
    setPreviewUrl(null);
    setPreviewName("");
    setExternalUrl("");
    setDesc("");
    setTags([]);
    setFavorite(false);
    setBulkQueue([]);
    setSelectedFiles([]);
    setUrlOpen(false);
    setDetailsOpen(false);
    setIsDragging(false);
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [list, m] = await Promise.all([
        api.get("/media-library"),
        api.get("/media-library/meta").catch(() => null),
      ]);
      setItems(list?.data?.items || []);
      setMeta(m?.data || { tags: [], max_items: 30 });
    } catch (_e) {
      toast.error("Could not load your media library");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (filterTags.length) {
        const owned = new Set((i.tags || []).map((t) => t.toLowerCase()));
        if (!filterTags.every((t) => owned.has(t.toLowerCase()))) return false;
      }
      if (q && !(i.description || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, filterTags, search]);

  // Single-file path (keeps the existing single-image dialog flow with
  // description/tags/favorite). Used when the user picks ONE file.
  const handleSingleFile = async (file) => {
    if (!file) return;
    if (!ACCEPTED_TYPES.has(file.type)) {
      toast.error(`Unsupported format. Please use ${ACCEPTED_LABEL}.`);
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("That image is too large (max 8 MB).");
      return;
    }
    try {
      const dataUrl = await fileToCompressedDataUrl(file);
      setPreviewUrl(dataUrl);
      setPreviewName(file.name || "");
      setExternalUrl("");
      // A single-file selection replaces any multi-file buffer.
      setSelectedFiles([]);
    } catch (_e) {
      toast.error("Could not read image");
    }
  };

  // Phase 16: build the multi-file buffer (thumbnails, filenames) BEFORE
  // uploading. Users click "Add N images" to actually upload — this makes
  // remove/change intuitive and gives instant feedback.
  const handleMultiFilesBuffer = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;
    if (files.length === 1) {
      // Fall back to the standard single-file editor so the user can add
      // tags + description + favorite before saving.
      return handleSingleFile(files[0]);
    }
    setPreparingFiles(true);
    const buffer = [];
    for (const f of files) {
      if (!ACCEPTED_TYPES.has(f.type)) {
        toast.error(`Skipped "${f.name}" — unsupported format (${ACCEPTED_LABEL}).`);
        continue;
      }
      if (f.size > 8 * 1024 * 1024) {
        toast.error(`Skipped "${f.name}" — larger than 8 MB.`);
        continue;
      }
      try {
        const dataUrl = await fileToCompressedDataUrl(f);
        buffer.push({ name: f.name, dataUrl, size: f.size });
      } catch (_e) {
        toast.error(`Could not read "${f.name}"`);
      }
    }
    // Clear the single-file path when switching to a multi-file selection.
    setPreviewUrl(null);
    setPreviewName("");
    setExternalUrl("");
    setSelectedFiles(buffer);
    setPreparingFiles(false);
  };

  // Runs the actual multi-file upload of the pre-built buffer. Sequential
  // by design (see original comment) — never fires parallel base64 payloads.
  const runMultiFileUpload = async () => {
    if (selectedFiles.length === 0) return;
    setBulkBusy(true);
    const initial = selectedFiles.map((f) => ({ name: f.name, status: "queued" }));
    setBulkQueue(initial);
    let okCount = 0;
    let failCount = 0;
    for (let i = 0; i < selectedFiles.length; i += 1) {
      const f = selectedFiles[i];
      const updateRow = (patch) =>
        setBulkQueue((q) => q.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
      updateRow({ status: "uploading" });
      try {
        await api.post("/media-library", { image_url: f.dataUrl });
        updateRow({ status: "done" });
        okCount += 1;
      } catch (err) {
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 409) {
          updateRow({ status: "error", error: detail || "Library is full" });
          setBulkQueue((q) =>
            q.map((r, idx) => (idx > i && r.status === "queued" ? { ...r, status: "error", error: "Library full" } : r)),
          );
          failCount += selectedFiles.length - i;
          break;
        }
        updateRow({ status: "error", error: detail || "Upload failed" });
        failCount += 1;
      }
    }
    if (okCount > 0) {
      toast.success(`Added ${okCount} photo${okCount === 1 ? "" : "s"} to your library`);
    }
    if (failCount > 0 && okCount === 0) {
      toast.error(`${failCount} upload${failCount === 1 ? "" : "s"} failed`);
    } else if (failCount > 0) {
      toast.warning(`Added ${okCount} · ${failCount} failed`);
    }
    setBulkBusy(false);
    if (failCount === 0) {
      resetDialog();
      setUploadOpen(false);
    }
    await refresh();
  };

  // Single-image submit (previewUrl / externalUrl path). Multi-file uploads
  // go through ``runMultiFileUpload`` — the CTA button routes to whichever
  // path is active based on ``selectedFiles.length``.
  const submit = async () => {
    if (selectedFiles.length > 0) {
      // Multi-file buffer path
      return runMultiFileUpload();
    }
    const image_url = (previewUrl || externalUrl || "").trim();
    if (!image_url) {
      toast.error("Choose an image first — upload one or paste a URL.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/media-library", {
        image_url,
        description: desc.trim() || null,
        tags,
        is_favorite: favorite,
      });
      toast.success("Added to your library");
      resetDialog();
      setUploadOpen(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save image");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleFavorite = async (item) => {
    try {
      await api.patch(`/media-library/${item.media_id}`, {
        is_favorite: !item.is_favorite,
      });
      refresh();
    } catch (_e) {
      toast.error("Could not update favorite");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/media-library/${confirmDelete.media_id}`);
      toast.success("Removed from library");
      setConfirmDelete(null);
      refresh();
    } catch (_e) {
      toast.error("Could not delete");
    }
  };

  const remaining = Math.max(0, (meta.max_items || 30) - items.length);

  return (
    <>
      <GradientMesh />
      <div className="space-y-6 pb-10" data-testid="media-library-page">
        <PageHeader
          title="Business Image Library"
          subtitle="Upload your own business photos once, then Postmoro can reuse them in future posts."
          testidPrefix="media-library"
          action={
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={refresh}
                disabled={loading}
                className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                data-testid="media-library-refresh"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              <Button
                onClick={() => setUploadOpen(true)}
                disabled={remaining === 0}
                title={remaining === 0 ? "Library is full — delete an item to add another" : undefined}
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                data-testid="media-library-add"
              >
                <ImagePlus className="h-4 w-4 mr-2" />
                Upload images
              </Button>
            </div>
          }
        />

        <GlassCard className="p-4" data-testid="media-library-status">
          <div className="flex flex-wrap items-center gap-3 text-xs text-[color:var(--text-2)]">
            <ImageIcon className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" />
            <span>
              <span className="font-mono text-[color:var(--text-1)]">{items.length}</span> of{" "}
              <span className="font-mono">{meta.max_items || 30}</span> images used
            </span>
            <span className="text-[color:var(--text-3)]">·</span>
            <span>{remaining} slots remaining</span>
            <span className="text-[color:var(--text-3)]">·</span>
            <span className="text-[color:var(--text-3)]">
              Used when you pick &quot;Use my library&quot; on Generate Post (no AI image credit needed).
            </span>
          </div>
        </GlassCard>

        <GlassCard className="p-4" data-testid="media-library-filter-card">
          <div className="flex flex-wrap items-center gap-3">
            <FilterIcon className="h-3.5 w-3.5 text-[color:var(--text-3)]" />
            <span className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)]">Filter</span>
            <div className="flex-1 min-w-[200px]">
              <TagPicker value={filterTags} onChange={setFilterTags} allTags={meta.tags || []} />
            </div>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search descriptions…"
              className="max-w-[240px] bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
              data-testid="media-library-search"
            />
          </div>
        </GlassCard>

        {loading ? (
          <div className="text-sm text-[color:var(--text-3)]">Loading…</div>
        ) : filtered.length === 0 ? (
          <GlassCard
            className="p-8 text-center"
            data-testid={items.length === 0 ? "media-library-empty-first-run" : "media-library-empty"}
          >
            <div className="h-12 w-12 rounded-full bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)] flex items-center justify-center mx-auto mb-3">
              <ImagePlus className="h-5 w-5 text-[color:var(--brand-primary)]" />
            </div>
            <div className="font-display text-lg font-semibold text-[color:var(--text-1)]">
              {items.length === 0
                ? "No business photos added yet."
                : "No images match the current filters"}
            </div>
            <div className="text-sm text-[color:var(--text-2)] mt-1 max-w-md mx-auto">
              {items.length === 0
                ? "Upload photos of your products, services, team, location, or past work so Postmoro can use them in posts."
                : "Try clearing tag filters or your search query."}
            </div>
            {items.length === 0 ? (
              <Button
                onClick={() => setUploadOpen(true)}
                className="mt-4 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                data-testid="media-library-empty-add"
              >
                <ImagePlus className="h-4 w-4 mr-2" />
                Add business photos
              </Button>
            ) : null}
          </GlassCard>
        ) : (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
            data-testid="media-library-grid"
          >
            {filtered.map((item) => (
              <div
                key={item.media_id}
                className="rounded-xl overflow-hidden border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] group"
                data-testid={`media-library-item-${item.media_id}`}
              >
                <div className="relative aspect-square bg-[color:var(--surface-2)]">
                  <img
                    src={item.image_url}
                    alt={item.description || "library image"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <button
                    type="button"
                    onClick={() => toggleFavorite(item)}
                    className={
                      "absolute top-2 left-2 h-7 w-7 rounded-full border flex items-center justify-center backdrop-blur " +
                      (item.is_favorite
                        ? "border-[color:rgba(245,193,30,0.55)] bg-[color:rgba(245,193,30,0.20)] text-yellow-300"
                        : "border-[color:var(--border-subtle)] bg-[color:var(--bg-1)]/60 text-[color:var(--text-3)] hover:text-[color:var(--text-1)]")
                    }
                    title={item.is_favorite ? "Unfavorite" : "Mark as favorite"}
                    data-testid={`media-library-favorite-${item.media_id}`}
                  >
                    <Star className={"h-3.5 w-3.5 " + (item.is_favorite ? "fill-current" : "")} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(item)}
                    className="absolute top-2 right-2 h-7 w-7 rounded-full border border-[color:rgba(255,92,108,0.45)] bg-[color:var(--bg-1)]/60 text-[color:var(--danger)] flex items-center justify-center backdrop-blur"
                    title="Remove from library"
                    data-testid={`media-library-delete-${item.media_id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="p-2.5">
                  <div className="text-xs text-[color:var(--text-2)] line-clamp-2 min-h-[2.4em]">
                    {item.description || (
                      <span className="text-[color:var(--text-3)]">No description</span>
                    )}
                  </div>
                  {item.tags?.length ? (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {item.tags.slice(0, 4).map((t) => (
                        <span
                          key={t}
                          className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-3)]"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload dialog — Phase 16 UX redesign.
         Design intent:
           • Upload area is the primary, obvious action at the top.
           • Selection preview (single or grid) is clear + has a Remove.
           • URL import is a secondary text-toggle, collapsed by default.
           • Description / Tags / Favorite live in a collapsed "Add details"
             section so the modal feels calm on open.
           • No exposed technical wording (compression, 1MB, 1280px).
           • Larger fonts, higher contrast, better touch targets.
      */}
      <Dialog
        open={uploadOpen}
        onOpenChange={(v) => {
          setUploadOpen(v);
          if (!v) resetDialog();
        }}
      >
        <DialogContent
          className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto bg-[color:var(--bg-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]"
          data-testid="media-library-upload-dialog"
        >
          <DialogHeader>
            <DialogTitle
              className="font-display text-lg text-[color:var(--text-1)]"
              data-testid="media-library-upload-title"
            >
              Add to image library
            </DialogTitle>
            <DialogDescription className="text-sm text-[color:var(--text-3)]">
              Add photos Postmoro can reuse when creating content.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* --- Primary: large upload / drop area -------------------- */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                if (!isDragging) setIsDragging(true);
              }}
              onDragLeave={(e) => {
                // Only clear when leaving the drop zone itself, not its children.
                if (e.currentTarget.contains(e.relatedTarget)) return;
                setIsDragging(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const files = e.dataTransfer?.files;
                if (files && files.length > 0) {
                  handleMultiFilesBuffer(files);
                }
              }}
              className={
                "relative rounded-lg border-2 border-dashed transition-colors px-6 py-8 text-center " +
                (isDragging
                  ? "border-[color:var(--brand-primary)] bg-[color:rgba(27,168,159,0.08)]"
                  : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] hover:border-[color:var(--border-strong)]")
              }
              data-testid="media-library-upload-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={(e) => handleMultiFilesBuffer(e.target.files)}
                className="sr-only"
                disabled={bulkBusy || preparingFiles}
                data-testid="media-library-upload-input"
              />
              <UploadCloud className="mx-auto h-8 w-8 text-[color:var(--brand-primary)]" aria-hidden="true" />
              <div className="mt-3 text-base font-medium text-[color:var(--text-1)]">
                Drag and drop an image here
              </div>
              <div className="mt-1 text-sm text-[color:var(--text-3)]">
                or
              </div>
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={bulkBusy || preparingFiles}
                className="mt-3 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold px-5 py-2 h-auto text-sm"
                data-testid="media-library-choose-file-button"
              >
                {preparingFiles ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Reading images…
                  </>
                ) : (
                  <>
                    <ImagePlus className="h-4 w-4 mr-2" />
                    Choose image
                  </>
                )}
              </Button>
              <div className="mt-3 text-xs text-[color:var(--text-3)]">
                JPG, PNG or WEBP · Postmoro automatically optimizes your image.
              </div>
            </div>

            {/* --- Single-image selection preview ---------------------- */}
            {previewUrl || externalUrl ? (
              <div
                className="rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 flex items-center gap-3"
                data-testid="media-library-selection-preview"
              >
                <img
                  src={previewUrl || externalUrl}
                  alt="Selected"
                  className="h-16 w-16 object-cover rounded-md border border-[color:var(--border-subtle)]"
                  data-testid="media-library-preview-img"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[color:var(--text-1)] truncate">
                    {previewName || (externalUrl ? "Image from URL" : "Selected image")}
                  </div>
                  <div className="text-xs text-[color:var(--text-3)] mt-0.5">
                    Ready to add to your library
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setPreviewUrl(null);
                    setPreviewName("");
                    setExternalUrl("");
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  aria-label="Remove selected image"
                  className="h-8 w-8 rounded-md flex items-center justify-center text-[color:var(--text-3)] hover:text-[color:var(--text-1)] hover:bg-[color:var(--surface-3)] transition"
                  data-testid="media-library-remove-selection"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </div>
            ) : null}

            {/* --- Multi-image selection grid -------------------------- */}
            {selectedFiles.length > 0 ? (
              <div
                className="rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3"
                data-testid="media-library-selection-grid"
              >
                <div className="flex items-center justify-between mb-2.5">
                  <div className="text-sm font-medium text-[color:var(--text-1)]">
                    {selectedFiles.length} image{selectedFiles.length === 1 ? "" : "s"} selected
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedFiles([]);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    className="text-xs text-[color:var(--text-3)] hover:text-[color:var(--text-1)] underline underline-offset-2"
                    data-testid="media-library-clear-selection"
                  >
                    Clear all
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {selectedFiles.map((f, idx) => (
                    <div
                      key={`${f.name}-${idx}`}
                      className="relative aspect-square rounded-md overflow-hidden border border-[color:var(--border-subtle)] group"
                    >
                      <img
                        src={f.dataUrl}
                        alt={f.name}
                        className="h-full w-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedFiles((prev) => prev.filter((_, i) => i !== idx))
                        }
                        aria-label={`Remove ${f.name}`}
                        className="absolute top-1 right-1 h-6 w-6 rounded-full bg-[color:rgba(0,0,0,0.65)] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
                        data-testid={`media-library-remove-file-${idx}`}
                      >
                        <XIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                {/* Multi-file details are intentionally skipped — the user
                   can edit each photo individually after upload. Keep the
                   modal simple. */}
                <div className="mt-2 text-xs text-[color:var(--text-3)]">
                  You can add descriptions or tags to each photo after uploading.
                </div>
              </div>
            ) : null}

            {/* --- Bulk upload progress list (during upload) ----------- */}
            {bulkQueue.length > 0 ? (
              <div
                className="rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 space-y-1.5 max-h-40 overflow-y-auto"
                data-testid="media-library-bulk-progress"
              >
                <div className="text-xs font-medium text-[color:var(--text-2)] mb-1">
                  Uploading…
                </div>
                {bulkQueue.map((row, idx) => (
                  <div
                    key={`${row.name}-${idx}`}
                    className="flex items-center gap-2 text-xs"
                  >
                    <span className="flex-1 truncate text-[color:var(--text-2)]">{row.name}</span>
                    {row.status === "queued" ? (
                      <span className="text-[color:var(--text-3)]">queued</span>
                    ) : row.status === "uploading" ? (
                      <span className="inline-flex items-center gap-1 text-[color:var(--brand-primary)]">
                        <Loader2 className="h-3 w-3 animate-spin" /> uploading
                      </span>
                    ) : row.status === "done" ? (
                      <span className="text-[color:var(--success,#3fbfa5)] font-medium">done</span>
                    ) : (
                      <span className="text-[color:var(--danger,#e5484d)] truncate max-w-[160px]" title={row.error}>
                        {row.error || "failed"}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : null}

            {/* --- Secondary: Import from URL (collapsed by default) --- */}
            {selectedFiles.length === 0 ? (
              <div className="pt-1">
                {!urlOpen ? (
                  <button
                    type="button"
                    onClick={() => setUrlOpen(true)}
                    className="inline-flex items-center gap-1.5 text-sm text-[color:var(--text-2)] hover:text-[color:var(--text-1)] transition"
                    data-testid="media-library-toggle-url"
                  >
                    <Link2 className="h-3.5 w-3.5" />
                    Import from URL
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label
                        htmlFor="ml-url-input"
                        className="text-sm font-medium text-[color:var(--text-1)]"
                      >
                        Import from URL
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          setUrlOpen(false);
                          setExternalUrl("");
                        }}
                        className="text-xs text-[color:var(--text-3)] hover:text-[color:var(--text-1)]"
                        data-testid="media-library-close-url"
                      >
                        Hide
                      </button>
                    </div>
                    <Input
                      id="ml-url-input"
                      value={externalUrl}
                      onChange={(e) => {
                        setExternalUrl(e.target.value);
                        setPreviewUrl(null);
                        setPreviewName("");
                      }}
                      placeholder="https://example.com/photo.jpg"
                      className="bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-sm h-10"
                      data-testid="media-library-url-input"
                    />
                  </div>
                )}
              </div>
            ) : null}

            {/* --- Add details (collapsible: description + tags + fav) - */}
            {selectedFiles.length === 0 ? (
              <div className="rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] overflow-hidden">
                <button
                  type="button"
                  onClick={() => setDetailsOpen((v) => !v)}
                  aria-expanded={detailsOpen}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-[color:var(--text-1)] hover:bg-[color:var(--surface-2)] transition"
                  data-testid="media-library-toggle-details"
                >
                  <span>
                    Add details{" "}
                    <span className="text-[color:var(--text-3)] font-normal">(optional)</span>
                  </span>
                  <ChevronDown
                    className={
                      "h-4 w-4 text-[color:var(--text-3)] transition-transform " +
                      (detailsOpen ? "rotate-180" : "rotate-0")
                    }
                  />
                </button>
                {detailsOpen ? (
                  <div className="px-4 pb-4 pt-1 space-y-4" data-testid="media-library-details-panel">
                    <div>
                      <label
                        htmlFor="ml-desc"
                        className="text-sm font-medium text-[color:var(--text-1)]"
                      >
                        Description{" "}
                        <span className="text-[color:var(--text-3)] font-normal">(optional)</span>
                      </label>
                      <Textarea
                        id="ml-desc"
                        value={desc}
                        onChange={(e) => setDesc(e.target.value)}
                        placeholder="e.g. Signature burger on the patio"
                        rows={2}
                        maxLength={500}
                        className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-sm"
                        data-testid="media-library-desc"
                      />
                      <div className="text-xs text-[color:var(--text-3)] mt-1">
                        Helps Postmoro choose the right image for future posts.
                      </div>
                    </div>

                    <div>
                      <div className="text-sm font-medium text-[color:var(--text-1)] flex items-center gap-1.5">
                        <TagIcon className="h-3.5 w-3.5 text-[color:var(--text-3)]" />
                        Tags{" "}
                        <span className="text-[color:var(--text-3)] font-normal">(optional)</span>
                      </div>
                      <div className="mt-2">
                        <TagPicker value={tags} onChange={setTags} allTags={meta.tags || []} />
                      </div>
                    </div>

                    {/* Favorite — obvious single toggle, not a tiny checkbox */}
                    <button
                      type="button"
                      onClick={() => setFavorite((v) => !v)}
                      aria-pressed={favorite}
                      className={
                        "inline-flex items-center gap-2 px-3 py-2 rounded-md border transition text-sm " +
                        (favorite
                          ? "bg-[color:rgba(255,201,0,0.10)] border-[color:rgba(255,201,0,0.55)] text-[color:var(--text-1)]"
                          : "bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)] hover:border-[color:var(--border-strong)]")
                      }
                      data-testid="media-library-favorite-toggle"
                    >
                      <Star
                        className={
                          "h-4 w-4 " +
                          (favorite ? "text-yellow-300 fill-yellow-300" : "text-[color:var(--text-3)]")
                        }
                      />
                      {favorite ? "Favorite" : "Mark as favorite"}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <DialogFooter className="mt-2 gap-2">
            <Button
              variant="outline"
              onClick={() => setUploadOpen(false)}
              disabled={submitting || bulkBusy}
              className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)] hover:bg-[color:var(--surface-3)]"
              data-testid="media-library-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={
                submitting ||
                bulkBusy ||
                preparingFiles ||
                (selectedFiles.length === 0 && !previewUrl && !externalUrl.trim())
              }
              className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold min-w-[140px]"
              data-testid="media-library-submit"
            >
              {submitting || bulkBusy ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {bulkBusy ? "Uploading…" : "Adding…"}
                </>
              ) : (
                <>
                  <ImagePlus className="h-4 w-4 mr-2" />
                  {selectedFiles.length > 1
                    ? `Add ${selectedFiles.length} images`
                    : "Add image"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        title="Remove from library?"
        description="This will only remove the image from your library. Posts that already used this image won't be affected."
        confirmLabel="Remove"
        cancelLabel="Keep"
        onConfirm={doDelete}
        testidPrefix="media-library-delete-confirm"
      />
    </>
  );
}
